import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Button,
  ButtonGroup,
  InlineStack,
  Select,
  Stack,
  Tag as TagChip,
  Text,
  useSnackbars,
} from '@customerio/pluma-components/react';
import type { Tag } from '@customerio/hydra-ui-api';
import { isConflictError } from '@/services/api/errors';
import { getWorkspaceTagsQueryOptions } from '@/services/db/tag/queries';
import { tagForName } from '../utils/tag-for-name';
import { useCreateTag } from '@/services/db/tag/mutations';
import { EditTrigger } from './edit-trigger';
import * as editTriggerStyles from './edit-trigger.css';
import * as styles from './tag-editor.css';
import { RowClickGuard } from './row-click-guard';
import { TagsCell } from './tags-cell';

/**
 * A row's tags, changed in place.
 *
 * The picker offers every tag the workspace has, not only the ones already
 * on rows of this list, and names a new one when none of them fit — which is
 * how a workspace's vocabulary grows in the first place.
 */

const NO_PERMISSION_TOOLTIP = 'Only workspace admins can edit tags.';
const DUPLICATE_MESSAGE = 'A tag with that name already exists.';
const CREATE_FAILED_MESSAGE = "That tag couldn't be created. Try again.";

export type TagEditorProps = {
  workspaceId: number;
  /**
   * Every tag id the row carries. The editor writes the whole set, so it
   * opens on the ids rather than on the tags below: an id the list could not
   * name would otherwise be unattached by the next save.
   */
  tagIds: readonly number[];
  /** The names the list response carried, which the workspace list tops up. */
  tags: readonly Tag[];
  /** False for a reserved name, or for a viewer without the permission. */
  canEdit: boolean;
  /** Whether the viewer would be allowed if the row were not reserved. */
  hasPermission: boolean;
  /** Resolves to whether the save went through; a failure keeps the picker open. */
  onSave: (tagIds: number[]) => Promise<boolean>;
  /**
   * Set while this row is one of several being edited together. The choice
   * then lives outside the cell, because one Save covers every row, so the
   * picker is always open and has no buttons of its own.
   */
  bulk?: {
    tagIds: number[];
    onChange: (next: (chosen: number[]) => number[]) => void;
    /**
     * Told while this cell is naming a tag. The one Save that covers every
     * row lives outside the cell, so it has to wait for the name the same way
     * the cell's own Save does.
     */
    onNaming: (isNaming: boolean) => void;
  };
};

export function TagEditor({ workspaceId, tagIds, tags, canEdit, hasPermission, onSave, bulk }: TagEditorProps) {
  const [ownDraft, setOwnDraft] = useState<number[] | null>(null);
  const { data: workspaceTags } = useQuery(getWorkspaceTagsQueryOptions(workspaceId));
  const named = namedTags(tagIds, tags, workspaceTags);

  // In a bulk edit the choice lives outside the cell; on its own the cell
  // holds it, and `null` is what "not editing" means.
  // A row nobody may edit shows its tags whichever mode the table is in; a
  // bulk edit never contains one, but the component says so itself.
  const draft = canEdit ? (bulk?.tagIds ?? ownDraft) : null;
  const change = (next: (chosen: number[]) => number[]) =>
    bulk == null ? setOwnDraft((current) => (current == null ? null : next(current))) : bulk.onChange(next);

  if (draft == null) {
    return (
      <RowClickGuard>
        <TagTrigger
          tags={named}
          canEdit={canEdit}
          hasPermission={hasPermission}
          onStart={() => setOwnDraft([...tagIds])}
        />
      </RowClickGuard>
    );
  }

  return (
    <TagPicker
      workspaceId={workspaceId}
      // What the caller could already name, so a chip never falls back to a
      // bare id while the workspace list is still out or has failed.
      known={named}
      chosen={draft}
      // Guarded: a tag created after Cancel must not reopen the picker.
      onChange={change}
      withButtons={bulk == null}
      onCancel={() => setOwnDraft(null)}
      onNaming={bulk?.onNaming}
      onSave={async () => {
        const saved = await onSave(draft);
        if (saved) {
          setOwnDraft(null);
        }
        return saved;
      }}
    />
  );
}

/**
 * The tags a row carries, named.
 *
 * The workspace list is preferred over the names the list response sideloads:
 * a tag created while editing is in the first and not yet in the second, and
 * a row whose tag cannot be named would otherwise lose it from the cell while
 * still carrying it. An id neither can name is left out rather than shown as
 * a number.
 */
function namedTags(
  tagIds: readonly number[],
  sideloaded: readonly Tag[],
  workspaceTags: readonly Tag[] | undefined,
): Tag[] {
  const byId = new Map(sideloaded.map((tag) => [tag.id, tag]));
  for (const tag of workspaceTags ?? []) {
    byId.set(tag.id, tag);
  }
  return tagIds.flatMap((id) => byId.get(id) ?? []);
}

type TagPickerProps = {
  workspaceId: number;
  /** The names the row arrived with, which the workspace list tops up. */
  known: readonly Tag[];
  chosen: number[];
  /** Told while a tag is being named, for a Save that lives outside the cell. */
  onNaming?: (isNaming: boolean) => void;
  onChange: (next: (chosen: number[]) => number[]) => void;
  /** False while several rows are saved together, which has one Save for all of them. */
  withButtons: boolean;
  onCancel: () => void;
  onSave: () => Promise<boolean>;
};

function TagPicker({ workspaceId, known, chosen, onChange, withButtons, onCancel, onSave, onNaming }: TagPickerProps) {
  const snackbars = useSnackbars();
  const [isSaving, setIsSaving] = useState(false);
  // Naming a tag covers more than the write: the workspace list is read again
  // first, and Save must not run in between — it would write the set without
  // the tag just named, and close the picker, leaving a tag created and
  // attached to nothing.
  const [isNaming, setIsNaming] = useState(false);
  const { data: workspaceTags, refetch: readWorkspaceTags } = useQuery(getWorkspaceTagsQueryOptions(workspaceId));
  const createTag = useCreateTag({ workspaceId });

  // A tag named a moment ago is in none of the reads yet, so the picker
  // remembers it: a chip showing its id instead of its name reads as a tag
  // called "4".
  const [created, setCreated] = useState<Tag[]>([]);
  const tagsById = new Map([...known, ...(workspaceTags ?? []), ...created].map((tag) => [tag.id, tag]));
  // One at a time, as the page this replaces does: picking a tag closes the
  // menu, which in a column this narrow is what keeps Save reachable.
  const unchosen = (workspaceTags ?? [])
    .filter((tag) => !chosen.includes(tag.id))
    .map((tag) => ({ label: tag.name, value: String(tag.id) }));

  /**
   * A typeahead keystroke that matches nothing answers with `null`, and the
   * id it would read as is `0` — a tag the workspace does not have, chipped
   * as "0" and written into the row by the next save.
   */
  const add = (value: string | null) => {
    const tagId = Number(value);
    if (value == null || !Number.isInteger(tagId) || tagId <= 0) {
      return;
    }
    onChange((current) => (current.includes(tagId) ? current : [...current, tagId]));
  };
  const remove = (tagId: number) => onChange((current) => current.filter((id) => id !== tagId));

  /** Attach the tag a name asks for, wherever it has to come from. */
  const create = async (name: string) => {
    const wanted = name.trim();
    setIsNaming(true);
    onNaming?.(true);
    try {
      const { tag, isNew } = await tagForName({
        wanted,
        inHand: [...known, ...(workspaceTags ?? []), ...created],
        reread: async () => (await readWorkspaceTags()).data,
        create: (named) => createTag.mutateAsync({ name: named }),
      });
      if (isNew) {
        setCreated((current) => [...current, tag]);
      }
      add(String(tag.id));
    } catch (error) {
      snackbars.create({
        id: 'data-index-create-tag',
        variant: 'critical',
        message: isConflictError(error) ? DUPLICATE_MESSAGE : CREATE_FAILED_MESSAGE,
      });
    } finally {
      setIsNaming(false);
      onNaming?.(false);
    }
  };

  const save = async () => {
    if (isNaming || isSaving) {
      return;
    }
    setIsSaving(true);
    await onSave();
    setIsSaving(false);
  };

  return (
    <RowClickGuard>
      <Stack gap="100">
        {chosen.length > 0 ? (
          <InlineStack gap="050" alignBlock="center">
            {chosen.map((tagId) => (
              <TagChip key={tagId} onDelete={() => remove(tagId)}>
                {tagsById.get(tagId)?.name ?? String(tagId)}
              </TagChip>
            ))}
          </InlineStack>
        ) : null}
        <Select
          ariaLabel="Tags"
          w="full"
          isSearchable
          withCreate
          value={null}
          placeholder="Choose or create a tag"
          searchPlaceholder="Find a tag…"
          createOptionLabel={(value) => `Create tag "${value.trim()}"`}
          options={unchosen}
          isDisabled={isSaving}
          isLoading={isNaming}
          onChange={(value) => add(value)}
          onCreate={(name) => void create(name)}
        />
        {withButtons ? (
          <ButtonGroup groupVariant="spaced">
            <Button
              variant="primary"
              size="sm"
              icon="check"
              isIconOnly
              aria-label="Save tags"
              isDisabled={isNaming}
              isLoading={isSaving}
              onClick={() => void save()}
            />
            <Button
              variant="secondary"
              size="sm"
              icon="close"
              isIconOnly
              aria-label="Cancel editing tags"
              isDisabled={isSaving}
              onClick={onCancel}
            />
          </ButtonGroup>
        ) : null}
      </Stack>
    </RowClickGuard>
  );
}

type TagTriggerProps = {
  tags: readonly Tag[];
  canEdit: boolean;
  hasPermission: boolean;
  onStart: () => void;
};

/** The tags as they read when nobody is editing them. */
function TagTrigger({ tags, canEdit, hasPermission, onStart }: TagTriggerProps) {
  return (
    <EditTrigger
      canEdit={canEdit}
      hasPermission={hasPermission}
      tooltip={NO_PERMISSION_TOOLTIP}
      onStart={onStart}
      className={styles.trigger}
    >
      {tags.length === 0 && canEdit ? (
        // The invitation reads as placeholder text at `xs`, as the page this
        // replaces writes it (`tags-cell/index.css.ts:9-16`); the tags
        // themselves are badges and keep their own colour.
        <Text className={editTriggerStyles.triggerLabel} size="xs" color="placeholder">
          Add tags
        </Text>
      ) : (
        <TagsCell tags={tags} />
      )}
    </EditTrigger>
  );
}
